import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { createAbsenceType, type CreateAbsenceTypeInput } from '@/lib/absences';
import { Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CreateAbsenceTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Predefined colors for absence types
const ABSENCE_COLORS = [
  { value: '#4CAF50', label: 'Green' },
  { value: '#2196F3', label: 'Blue' },
  { value: '#FF5722', label: 'Orange' },
  { value: '#9C27B0', label: 'Purple' },
  { value: '#FF9800', label: 'Amber' },
  { value: '#607D8B', label: 'Gray' },
  { value: '#E91E63', label: 'Pink' },
  { value: '#00BCD4', label: 'Cyan' },
];

export function CreateAbsenceTypeDialog({ open, onOpenChange }: CreateAbsenceTypeDialogProps) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [isPaid, setIsPaid] = useState(true);
  const [defaultDays, setDefaultDays] = useState('');
  const [color, setColor] = useState(ABSENCE_COLORS[0].value);
  const [errors, setErrors] = useState<{ name?: string; server?: string }>({});

  const resetForm = () => {
    setName('');
    setIsPaid(true);
    setDefaultDays('');
    setColor(ABSENCE_COLORS[0].value);
    setErrors({});
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateAbsenceTypeInput) => createAbsenceType(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['absence-types'] });
      resetForm();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      setErrors({ server: error.message || 'Failed to create absence type' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    const input: CreateAbsenceTypeInput = {
      name: name.trim(),
      isPaid,
      color,
    };

    if (defaultDays) {
      input.defaultDays = parseInt(defaultDays, 10);
    }

    createMutation.mutate(input);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Absence Type</DialogTitle>
          <DialogDescription>
            Add a new type of absence for your organization.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.server && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {errors.server}
            </div>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-xs font-medium text-muted-foreground">
              Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) setErrors({});
              }}
              disabled={createMutation.isPending}
              placeholder="e.g., Sick Leave"
              className={cn('h-9', errors.name && 'border-destructive')}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name}</p>
            )}
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">Color</Label>
            <div className="flex gap-2 flex-wrap">
              {ABSENCE_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  className={cn(
                    'w-8 h-8 rounded-full border-2 cursor-pointer transition-transform hover:scale-110',
                    color === c.value ? 'border-foreground scale-110' : 'border-transparent'
                  )}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                />
              ))}
            </div>
          </div>

          {/* Is Paid */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="isPaid"
              checked={isPaid}
              onCheckedChange={(checked) => setIsPaid(checked === true)}
              disabled={createMutation.isPending}
            />
            <Label htmlFor="isPaid" className="text-sm font-normal cursor-pointer">
              Paid leave
            </Label>
          </div>

          {/* Default Days */}
          <div className="space-y-1.5">
            <Label htmlFor="defaultDays" className="text-xs font-medium text-muted-foreground">
              Default days per year
            </Label>
            <Input
              id="defaultDays"
              type="number"
              min="0"
              max="365"
              value={defaultDays}
              onChange={(e) => setDefaultDays(e.target.value)}
              disabled={createMutation.isPending}
              placeholder="e.g., 20"
              className="h-9"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createMutation.isPending}
              className="cursor-pointer"
            >
              {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
